import { computed, ref } from 'vue';

import { INTEGRATIONS_BY_STATE, SEED_TO_SALE_VENDORS } from '@/constants';
import {
  BadgeCount,
  Company,
  CompanyBillingStatuses,
  CompanyStatuses,
  CompanyStatusReason,
  PaymentOption,
} from '@/models/company';
import { PaymentProgramChangeRequest, PaymentProgramChangeRequestStatuses } from '@/models/paymentProgramChangeRequest';
import { Staff } from '@/models/staff';

export function getInitialBadgeCountState(): BadgeCount {
  return {
    acceptedOrders: 0,
    activeOrders: 0,
    backorders: 0,
    collaborativeOrders: 0,
    combinedOrders: 0,
    completeOrders: 0,
    draftOrders: 0,
    fulfilledOrders: 0,
    pendingRequests: 0,
    shippedOrders: 0,
    submittedOrders: 0,
    submittedReturns: 0,
    totalFromLast7Days: {
      currency: '',
      total: 0,
    },
  };
}

export const setupCompanyState = () => {
  const activeCompany = ref<Company | undefined>();
  const activeCompanyId = ref<string | number>('');
  const availableProductIds = ref<number[]>([]);
  const badgeCounts = ref<BadgeCount>(getInitialBadgeCountState());
  const companies = ref<Company[]>([]);
  const isLoadingCompanies = ref(false);
  const isLoadingPaymentOptions = ref(false);
  const paymentOptions = ref<PaymentOption[]>([]);
  const staff = ref<Staff[]>([]);
  const isAppLoaded = ref(false);
  const paymentProgramChangeRequests = ref<PaymentProgramChangeRequest[]>([]);

  const integrationType = computed(() => activeCompany.value?.seedToSaleVendor || SEED_TO_SALE_VENDORS.METRC);
  const isMetrcEnabled = computed(() => integrationType.value === SEED_TO_SALE_VENDORS.METRC);
  const isBiotrackEnabled = computed(() => integrationType.value === SEED_TO_SALE_VENDORS.BIOTRACK);

  const getCompanyById = computed(
    () =>
      (companyId: number): Company | undefined =>
        companies.value.find(({ id }) => id === companyId),
  );

  const getCompanyHasDefaultPaymentOption = computed(() => paymentOptions.value.some(({ isDefault }) => isDefault));

  const isDelinquent = computed(() => (isLockdownSplitEnabled: boolean) => {
    if (!isLockdownSplitEnabled) {
      return false;
    }

    return (
      activeCompany.value?.status === CompanyStatuses.Inactive &&
      activeCompany.value?.billingStatus === CompanyBillingStatuses.Delinquent
    );
  });

  const isLicenseLocked = computed(
    () => (isPauseInactiveLicenseCompanySplitEnabled: boolean) =>
      isPauseInactiveLicenseCompanySplitEnabled &&
      activeCompany.value?.status === CompanyStatuses.Inactive &&
      activeCompany.value.statusReason === CompanyStatusReason.LicenseInactive,
  );

  const pendingPaymentProgramRequests = computed(() => {
    if (!activeCompany.value || activeCompany.value?.enablePaymentsDirect) {
      return [];
    }

    return paymentProgramChangeRequests.value.filter((request) => {
      return request.status === PaymentProgramChangeRequestStatuses.Pending;
    });
  });

  const isInSeedToSaleState = computed(() => {
    return activeCompany.value?.state && Object.keys(INTEGRATIONS_BY_STATE).includes(activeCompany.value?.state);
  });

  return {
    activeCompany,
    activeCompanyId,
    availableProductIds,
    badgeCounts,
    companies,
    integrationType,
    isAppLoaded,
    isBiotrackEnabled,
    isInSeedToSaleState,
    isLicenseLocked,
    isLoadingCompanies,
    isLoadingPaymentOptions,
    isMetrcEnabled,
    paymentOptions,
    staff,
    paymentProgramChangeRequests,

    isDelinquent,
    getCompanyById,
    getCompanyHasDefaultPaymentOption,
    pendingPaymentProgramRequests,
  };
};

export type CompanyState = ReturnType<typeof setupCompanyState>;
